import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Check, X, Mail, Phone, User, Key, Globe, Users, Shield, FileText, MapPin, Gift, XCircle, CheckCircle, Lock } from 'lucide-react';
import { database, ref, set, get, push, update, auth, createUserWithEmailAndPassword, signInWithEmailAndPassword } from '../../firebase';
// Email notifications disabled (no emailConfig)

// Dialog Component with updated colors
const Dialog = ({ isOpen, onClose, title, message, type = 'success' }) => {
  if (!isOpen) return null;

  const getDialogStyles = () => {
    const bgColor = '#132131';
    const textColor = 'text-white';
    
    switch (type) {
      case 'error':
        return {
          bg: 'bg-[#132131]',
          border: 'border-red-500/30',
          icon: <XCircle className="text-red-400" size={40} />,
          titleColor: `${textColor}`,
          textColor: 'text-gray-200'
        };
      case 'warning':
        return {
          bg: 'bg-[#132131]',
          border: 'border-yellow-500/30',
          icon: <div className="text-yellow-400 text-xl">⚠️</div>,
          titleColor: `${textColor}`,
          textColor: 'text-gray-200'
        };
      default: // success
        return {
          bg: 'bg-[#132131]',
          border: 'border-green-500/30',
          icon: <CheckCircle className="text-green-400" size={40} />,
          titleColor: `${textColor}`,
          textColor: 'text-gray-200'
        };
    }
  };

  const styles = getDialogStyles();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Dialog */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        className={`relative z-10 w-full max-w-sm rounded-xl ${styles.bg} border ${styles.border} p-6 shadow-2xl`}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute right-3 top-3 text-gray-400 hover:text-white transition-colors"
        >
          <X size={20} />
        </button>

        {/* Content */}
        <div className="text-center">
          <div className="flex justify-center mb-4">
            {styles.icon}
          </div>
          
          <h3 className={`text-xl font-bold mb-3 font-heading ${styles.titleColor}`}>
            {title}
          </h3>
          
          <div className={`text-sm mb-6 font-body ${styles.textColor} whitespace-pre-line`}>
            {message}
          </div>
          
          <button
            onClick={onClose}
            className={`px-6 py-2 rounded-lg font-heading font-medium text-sm transition-all ${
              type === 'success' 
                ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white' 
                : type === 'error'
                ? 'bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white'
                : 'bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 text-white'
            }`}
          >
            {type === 'success' ? 'Continue' : 'Try Again'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// Real-time Location Service with IP and Browser Geolocation
const getCurrentLocation = () => {
  return new Promise(async (resolve, reject) => {
    try {
      const fetchIpLocation = async () => {
        // 1) geolocation-db (CORS friendly)
        try {
          const res = await fetch('https://geolocation-db.com/json/');
          if (res.ok) {
            const data = await res.json();
            return {
              ipAddress: data?.IPv4 || 'Unknown',
              country: data?.country_name || 'Unknown',
              countryCode: data?.country_code || '',
              region: data?.state || 'Unknown',
              city: data?.city || 'Unknown',
              latitude: parseFloat(data?.latitude) || null,
              longitude: parseFloat(data?.longitude) || null,
              postalCode: data?.postal || '',
              timezone: 'Unknown',
              isp: 'Unknown',
              source: 'geolocation-db'
            };
          }
        } catch (e) {}

        // 2) ipinfo.io (fallback, may be rate limited)
        try {
          const res = await fetch('https://ipinfo.io/json');
          if (res.ok) {
            const data = await res.json();
            const [lat, lon] = (data?.loc || '').split(',');
            return {
              ipAddress: data?.ip || 'Unknown',
              country: data?.country || 'Unknown',
              countryCode: data?.country || '',
              region: data?.region || 'Unknown',
              city: data?.city || 'Unknown',
              latitude: lat ? parseFloat(lat) : null,
              longitude: lon ? parseFloat(lon) : null,
              postalCode: data?.postal || '',
              timezone: data?.timezone || 'Unknown',
              isp: data?.org || 'Unknown',
              source: 'ipinfo.io'
            };
          }
        } catch (e) {}

        return {
          ipAddress: 'Unknown',
          country: 'Unknown',
          countryCode: '',
          region: 'Unknown',
          city: 'Unknown',
          latitude: null,
          longitude: null,
          postalCode: '',
          timezone: 'Unknown',
          isp: 'Unknown',
          source: 'none'
        };
      };

      const ipLocationData = await fetchIpLocation();

      // Store IP-based location
      const ipLocation = {
        ipAddress: ipLocationData.ipAddress || 'Unknown',
        country: ipLocationData.country || 'Unknown',
        countryCode: ipLocationData.countryCode || '',
        region: ipLocationData.region || 'Unknown',
        city: ipLocationData.city || 'Unknown',
        latitude: ipLocationData.latitude ?? null,
        longitude: ipLocationData.longitude ?? null,
        postalCode: ipLocationData.postalCode || '',
        timezone: ipLocationData.timezone || 'Unknown',
        isp: ipLocationData.isp || 'Unknown',
        locationSource: `IP Geolocation (${ipLocationData.source})`,
        timestamp: new Date().toISOString()
      };

      // Now try browser geolocation for more accuracy
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;
            let reverseGeo = null;

            // Reverse geocode GPS to get real city/region/country
            try {
              const reverseResponse = await fetch(
                `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
              );
              reverseGeo = await reverseResponse.json();
            } catch (reverseError) {
              console.warn('Reverse geocoding failed, using IP location for city/region:', reverseError);
            }
            
            // Combine IP location with GPS coordinates
            const combinedLocation = {
              ...ipLocation,
              country: reverseGeo?.countryName || ipLocation.country,
              countryCode: reverseGeo?.countryCode || ipLocation.countryCode,
              region: reverseGeo?.principalSubdivision || ipLocation.region,
              city: reverseGeo?.city || reverseGeo?.locality || ipLocation.city,
              postalCode: reverseGeo?.postcode || ipLocation.postalCode,
              gpsLatitude: latitude,
              gpsLongitude: longitude,
              gpsAccuracy: position.coords.accuracy,
              locationSource: reverseGeo ? 'GPS Reverse Geocoding' : 'IP + GPS Geolocation',
              isGPSAvailable: true
            };

            console.log('📍 Combined location data:', combinedLocation);
            resolve(combinedLocation);
          },
          (gpsError) => {
            console.warn('GPS location failed, using IP location:', gpsError.message);
            resolve(ipLocation);
          },
          {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0
          }
        );
      } else {
        console.log('Geolocation not available, using IP location only');
        resolve(ipLocation);
      }
    } catch (error) {
      console.error('Location service error:', error);
      
      // Minimal fallback if everything fails
      const fallbackLocation = {
        ipAddress: 'Failed to fetch',
        country: 'Unknown',
        countryCode: '',
        region: 'Unknown',
        city: 'Unknown',
        latitude: null,
        longitude: null,
        postalCode: '',
        locationSource: 'Fallback',
        timestamp: new Date().toISOString()
      };
      resolve(fallbackLocation);
    }
  });
};

// Function to create fund transaction record for bonus with date-wise structure
const createFundTransactionRecord = async (userData, amount, type = 'bonus') => {
  try {
    const transactionId = `FUND_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const date = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
    
    // Create date-wise structure: UserFundTransactions/date/userId/transactionId
    const transactionRef = ref(database, `UserFundTransactions/${date}/${userData.sponsorId}/${transactionId}`);
    
    const transactionRecord = {
      transactionId: transactionId,
      timestamp: Date.now(),
      timestampFormatted: new Date().toISOString(),
      userId: userData.sponsorId,
      userName: userData.name,
      memberId: userData.dbKey,
      userStatus: 'inactive',
      amount: amount,
      type: type,
      source: 'Aerovolt',
      description: `Welcome bonus for new registration`,
      wallet: 'Fund Wallet',
      previousFundBalance: 0,
      fundBalance: amount,
      userAddedFund: amount,
      fundCount: 1,
      lastFundAmount: amount,
      lastFundDate: new Date().toISOString(),
      paymentMethod: 'bonus',
      paymentDisplayMethod: 'Registration Welcome Bonus',
      paymentId: `BONUS_${userData.sponsorId}`,
      network: 'Aerovolt'
    };
    
    await set(transactionRef, transactionRecord);
    console.log('Fund transaction record created with date-wise structure:', date, transactionId);
    return transactionId;
  } catch (error) {
    console.error('Error creating fund transaction record:', error);
    return null;
  }
};

// Function to generate unique database key with suffix for same names
const generateUniqueDbKey = async (name) => {
  const baseKey = name.trim().toLowerCase().replace(/\s+/g, '_');
  let dbKey = baseKey;
  let counter = 1;
  
  try {
    // Check ALL users across ALL dates
    const usersRef = ref(database, 'users');
    const snapshot = await get(usersRef);
    
    if (snapshot.exists()) {
      const usersByDate = snapshot.val();
      const existingKeys = [];
      
      // Collect ALL keys from ALL dates
      for (const date in usersByDate) {
        const users = usersByDate[date];
        existingKeys.push(...Object.keys(users));
      }
      
      // Find all keys that start with baseKey
      const matchingKeys = existingKeys.filter(key => key.startsWith(baseKey));
      
      if (matchingKeys.length > 0) {
        // Find the highest suffix number
        let maxSuffix = 0;
        
        for (const key of matchingKeys) {
          if (key === baseKey) {
            maxSuffix = Math.max(maxSuffix, 1);
          } else {
            // Check for pattern baseKey-1, baseKey-2, etc.
            const suffixMatch = key.match(new RegExp(`^${baseKey}-(\\d+)$`));
            if (suffixMatch) {
              const suffixNum = parseInt(suffixMatch[1]);
              if (!isNaN(suffixNum)) {
                maxSuffix = Math.max(maxSuffix, suffixNum);
              }
            }
          }
        }
        
        if (maxSuffix === 0) {
          // Base key exists but no suffix, so use -1
          dbKey = `${baseKey}-1`;
        } else {
          // Use next number
          dbKey = `${baseKey}-${maxSuffix + 1}`;
        }
      }
    }
    
    console.log('Generated dbKey:', dbKey, 'for name:', name);
    return dbKey;
  } catch (error) {
    console.error('Error generating unique dbKey:', error);
    return `${baseKey}-${Date.now()}`; // Fallback with timestamp
  }
};

const Register = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [form, setForm] = useState({
    sponsorId: queryParams.get('sponsor') || '',
    name: '',
    email: '',
    mobile: '',
    country: 'India',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeRefund, setAgreeRefund] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [mobileError, setMobileError] = useState('');
  const [sponsorIdError, setSponsorIdError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState({
    hasUpper: false,
    hasLower: false,
    hasNumber: false,
    hasSpecial: false,
    hasMinLength: false
  });
  const [generatedSponsorId, setGeneratedSponsorId] = useState('');
  const [sponsorName, setSponsorName] = useState('');
  // emailSent state kept for future email tracking
  const [emailSent, setEmailSent] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [registrationLocation, setRegistrationLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [bonusStatus, setBonusStatus] = useState('');
  const [generatedDbKey, setGeneratedDbKey] = useState('');
  
  // Dialog states
  const [dialog, setDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'success'
  });
  
  const navigate = useNavigate();
  const mobileInputRef = useRef(null);

const countries = [
  'Afghanistan',
  'Albania',
  'Algeria',
  'Andorra',
  'Angola',
  'Antigua and Barbuda',
  'Argentina',
  'Armenia',
  'Australia',
  'Austria',
  'Azerbaijan',
  'Bahamas',
  'Bahrain',
  'Bangladesh',
  'Barbados',
  'Belarus',
  'Belgium',
  'Belize',
  'Benin',
  'Bhutan',
  'Bolivia',
  'Bosnia and Herzegovina',
  'Botswana',
  'Brazil',
  'Brunei',
  'Bulgaria',
  'Burkina Faso',
  'Burundi',
  'Cabo Verde',
  'Cambodia',
  'Cameroon',
  'Canada',
  'Central African Republic',
  'Chad',
  'Chile',
  'China',
  'Colombia',
  'Comoros',
  'Congo',
  'Costa Rica',
  "Cote d'Ivoire",
  'Croatia',
  'Cuba',
  'Cyprus',
  'Czech Republic',
  'Democratic Republic of the Congo',
  'Denmark',
  'Djibouti',
  'Dominica',
  'Dominican Republic',
  'Ecuador',
  'Egypt',
  'El Salvador',
  'Equatorial Guinea',
  'Eritrea',
  'Estonia',
  'Eswatini',
  'Ethiopia',
  'Fiji',
  'Finland',
  'France',
  'Gabon',
  'Gambia',
  'Georgia',
  'Germany',
  'Ghana',
  'Greece',
  'Grenada',
  'Guatemala',
  'Guinea',
  'Guinea-Bissau',
  'Guyana',
  'Haiti',
  'Honduras',
  'Hungary',
  'Iceland',
  'India',
  'Indonesia',
  'Iran',
  'Iraq',
  'Ireland',
  'Israel',
  'Italy',
  'Jamaica',
  'Japan',
  'Jordan',
  'Kazakhstan',
  'Kenya',
  'Kiribati',
  'Kuwait',
  'Kyrgyzstan',
  'Laos',
  'Latvia',
  'Lebanon',
  'Lesotho',
  'Liberia',
  'Libya',
  'Liechtenstein',
  'Lithuania',
  'Luxembourg',
  'Madagascar',
  'Malawi',
  'Malaysia',
  'Maldives',
  'Mali',
  'Malta',
  'Marshall Islands',
  'Mauritania',
  'Mauritius',
  'Mexico',
  'Micronesia',
  'Moldova',
  'Monaco',
  'Mongolia',
  'Montenegro',
  'Morocco',
  'Mozambique',
  'Myanmar',
  'Namibia',
  'Nauru',
  'Nepal',
  'Netherlands',
  'New Zealand',
  'Nicaragua',
  'Niger',
  'Nigeria',
  'North Korea',
  'North Macedonia',
  'Norway',
  'Oman',
  'Pakistan',
  'Palau',
  'Palestine',
  'Panama',
  'Papua New Guinea',
  'Paraguay',
  'Peru',
  'Philippines',
  'Poland',
  'Portugal',
  'Qatar',
  'Romania',
  'Russia',
  'Rwanda',
  'Saint Kitts and Nevis',
  'Saint Lucia',
  'Saint Vincent and the Grenadines',
  'Samoa',
  'San Marino',
  'Sao Tome and Principe',
  'Saudi Arabia',
  'Senegal',
  'Serbia',
  'Seychelles',
  'Sierra Leone',
  'Singapore',
  'Slovakia',
  'Slovenia',
  'Solomon Islands',
  'Somalia',
  'South Africa',
  'South Korea',
  'South Sudan',
  'Spain',
  'Sri Lanka',
  'Sudan',
  'Suriname',
  'Sweden',
  'Switzerland',
  'Syria',
  'Tajikistan',
  'Tanzania',
  'Thailand',
  'Timor-Leste',
  'Togo',
  'Tonga',
  'Trinidad and Tobago',
  'Tunisia',
  'Turkey',
  'Turkmenistan',
  'Tuvalu',
  'Uganda',
  'Ukraine',
  'United Arab Emirates',
  'United Kingdom',
  'United States',
  'Uruguay',
  'Uzbekistan',
  'Vanuatu',
  'Vatican City',
  'Venezuela',
  'Vietnam',
  'Yemen',
  'Zambia',
  'Zimbabwe',
  'Others'
];

  // Email notifications disabled

  // Get user's real-time location on component mount
  useEffect(() => {
    const fetchLocation = async () => {
      try {
        setLocationLoading(true);
        setLocationError('');
        
        const locationData = await getCurrentLocation();
        setRegistrationLocation(locationData);
        
        // Auto-fill country if detected
        if (locationData.country && locationData.country !== 'Unknown') {
          setForm(prev => ({ ...prev, country: locationData.country }));
        }
        
        console.log('📍 Registration location:', locationData);
      } catch (error) {
        console.error('Failed to get location:', error);
        setLocationError('Failed to get your location. Please ensure location services are enabled.');
      } finally {
        setLocationLoading(false);
      }
    };
    
    fetchLocation();
  }, []);

  // Show dialog function
  const showDialog = (title, message, type = 'success') => {
    setDialog({
      isOpen: true,
      title,
      message,
      type
    });
  };

  // Close dialog function
  const closeDialog = () => {
    setDialog({
      isOpen: false,
      title: '',
      message: '',
      type: 'success'
    });
  };

  // Generate Sponsor ID (AV + 6 digits)
  const generateSponsorId = () => {
    const prefix = 'AV';
    const randomDigits = Math.floor(100000 + Math.random() * 900000);
    return `${prefix}${randomDigits}`;
  };

  // Generate unique Sponsor ID
  const generateUniqueSponsorId = async () => {
    let sponsorId = '';
    let attempts = 0;
    const maxAttempts = 10;
    
    while (attempts < maxAttempts) {
      sponsorId = generateSponsorId();
      const exists = await checkSponsorIdExists(sponsorId);
      if (!exists) break;
      attempts++;
    }
    
    if (attempts === maxAttempts) {
      sponsorId = 'AV' + Date.now().toString().slice(-6);
    }
    
    setGeneratedSponsorId(sponsorId);
    return sponsorId;
  };

  // Check if Sponsor ID exists (case insensitive)
  const checkSponsorIdExists = async (sponsorId) => {
    try {
      // Check in all date folders
      const usersRef = ref(database, 'users');
      const snapshot = await get(usersRef);
      
      if (snapshot.exists()) {
        const usersByDate = snapshot.val();
        
        // Iterate through all date folders
        for (const date in usersByDate) {
          const users = usersByDate[date];
          for (const userKey in users) {
            const user = users[userKey];
            if (user.sponsorId && user.sponsorId.toLowerCase() === sponsorId.toLowerCase()) {
              return true;
            }
          }
        }
      }
      return false;
    } catch (error) {
      console.error('Error checking sponsor ID:', error);
      return false;
    }
  };

const getCountryMaxLength = (country) => {
  const maxLengths = {
    'Afghanistan': 9,
    'Albania': 9,
    'Algeria': 9,
    'Andorra': 9,
    'Angola': 9,
    'Antigua and Barbuda': 10,
    'Argentina': 10,
    'Armenia': 8,
    'Australia': 9,
    'Austria': 10,
    'Azerbaijan': 9,
    'Bahamas': 10,
    'Bahrain': 8,
    'Bangladesh': 10,
    'Barbados': 10,
    'Belarus': 9,
    'Belgium': 9,
    'Belize': 7,
    'Benin': 8,
    'Bhutan': 8,
    'Bolivia': 8,
    'Bosnia and Herzegovina': 8,
    'Botswana': 8,
    'Brazil': 11,
    'Brunei': 7,
    'Bulgaria': 9,
    'Burkina Faso': 8,
    'Burundi': 8,
    'Cabo Verde': 7,
    'Cambodia': 9,
    'Cameroon': 9,
    'Canada': 10,
    'Central African Republic': 8,
    'Chad': 8,
    'Chile': 9,
    'China': 11,
    'Colombia': 10,
    'Comoros': 7,
    'Congo (Congo-Brazzaville)': 9,
    'Congo': 9,
    'Costa Rica': 8,
    "Cote d'Ivoire": 10,
    'Croatia': 9,
    'Cuba': 8,
    'Cyprus': 8,
    'Czechia (Czech Republic)': 9,
    'Czech Republic': 9,
    'Democratic Republic of the Congo': 9,
    'Denmark': 8,
    'Djibouti': 8,
    'Dominica': 10,
    'Dominican Republic': 10,
    'Ecuador': 9,
    'Egypt': 10,
    'El Salvador': 8,
    'Equatorial Guinea': 9,
    'Eritrea': 7,
    'Estonia': 8,
    'Eswatini (fmr. "Swaziland")': 8,
    'Eswatini': 8,
    'Ethiopia': 9,
    'Fiji': 7,
    'Finland': 10,
    'France': 9,
    'Gabon': 7,
    'Gambia': 7,
    'Georgia': 9,
    'Germany': 11,
    'Ghana': 9,
    'Greece': 10,
    'Grenada': 10,
    'Guatemala': 8,
    'Guinea': 9,
    'Guinea-Bissau': 7,
    'Guyana': 7,
    'Haiti': 8,
    'Honduras': 8,
    'Hungary': 9,
    'Iceland': 7,
    'India': 10,
    'Indonesia': 11,
    'Iran': 10,
    'Iraq': 10,
    'Ireland': 9,
    'Israel': 9,
    'Italy': 10,
    'Jamaica': 10,
    'Japan': 10,
    'Jordan': 9,
    'Kazakhstan': 10,
    'Kenya': 9,
    'Kiribati': 5,
    'Kuwait': 8,
    'Kyrgyzstan': 9,
    'Laos': 10,
    'Latvia': 8,
    'Lebanon': 8,
    'Lesotho': 8,
    'Liberia': 7,
    'Libya': 10,
    'Liechtenstein': 7,
    'Lithuania': 8,
    'Luxembourg': 9,
    'Madagascar': 9,
    'Malawi': 9,
    'Malaysia': 10,
    'Maldives': 7,
    'Mali': 8,
    'Malta': 8,
    'Marshall Islands': 7,
    'Mauritania': 8,
    'Mauritius': 8,
    'Mexico': 10,
    'Micronesia': 7,
    'Moldova': 8,
    'Monaco': 8,
    'Mongolia': 8,
    'Montenegro': 8,
    'Morocco': 9,
    'Mozambique': 9,
    'Myanmar (formerly Burma)': 9,
    'Myanmar': 9,
    'Namibia': 9,
    'Nauru': 7,
    'Nepal': 10,
    'Netherlands': 9,
    'New Zealand': 9,
    'Nicaragua': 8,
    'Niger': 8,
    'Nigeria': 10,
    'North Korea': 9,
    'North Macedonia (formerly Macedonia)': 8,
    'North Macedonia': 8,
    'Norway': 8,
    'Oman': 8,
    'Pakistan': 10,
    'Palau': 7,
    'Palestine State': 9,
    'Palestine': 9,
    'Panama': 8,
    'Papua New Guinea': 8,
    'Paraguay': 9,
    'Peru': 9,
    'Philippines': 10,
    'Poland': 9,
    'Portugal': 9,
    'Qatar': 8,
    'Romania': 10,
    'Russia': 10,
    'Rwanda': 9,
    'Saint Kitts and Nevis': 10,
    'Saint Lucia': 10,
    'Saint Vincent and the Grenadines': 10,
    'Samoa': 7,
    'San Marino': 10,
    'Sao Tome and Principe': 7,
    'Saudi Arabia': 9,
    'Senegal': 9,
    'Serbia': 9,
    'Seychelles': 7,
    'Sierra Leone': 8,
    'Singapore': 8,
    'Slovakia': 9,
    'Slovenia': 8,
    'Solomon Islands': 7,
    'Somalia': 9,
    'South Africa': 9,
    'South Korea': 10,
    'South Sudan': 9,
    'Spain': 9,
    'Sri Lanka': 9,
    'Sudan': 9,
    'Suriname': 7,
    'Sweden': 9,
    'Switzerland': 9,
    'Syria': 9,
    'Taiwan': 9,
    'Tajikistan': 9,
    'Tanzania': 9,
    'Thailand': 9,
    'Timor-Leste': 8,
    'Togo': 8,
    'Tonga': 5,
    'Trinidad and Tobago': 10,
    'Tunisia': 8,
    'Turkey': 10,
    'Turkmenistan': 8,
    'Tuvalu': 5,
    'Uganda': 9,
    'Ukraine': 10,
    'United Arab Emirates': 9,
    'United Kingdom': 10,
    'United States of America': 10,
    'United States': 10,
    'Uruguay': 9,
    'Uzbekistan': 9,
    'Vanuatu': 7,
    'Vatican City (Holy See)': 10,
    'Vatican City': 10,
    'Venezuela': 10,
    'Vietnam': 10,
    'Yemen': 9,
    'Zambia': 9,
    'Zimbabwe': 9,
    'Others': 10
  };
  
  return maxLengths[country] || 10;
};

// Email sending disabled
const sendWelcomeEmail = async () => {
  setEmailSent(false);
  return true;
};

  // Check if Sponsor exists (case insensitive) - DOWNLINE ACTIVATION LOGIC REMOVED
  const checkSponsorExists = async (sponsorId) => {
    if (!sponsorId.trim()) {
      setSponsorName('');
      setSponsorIdError('');
      return false;
    }
    
    // If sponsor ID is AGSTRA0001 (default for first user)
    if (sponsorId.toUpperCase() === 'AEROV001') {
      const isFirstUser = await checkIfFirstUser();
      if (isFirstUser) {
        setSponsorName('System Default (First User)');
        setSponsorIdError('');
        return true;
      } else {
        setSponsorName('');
        setSponsorIdError('AEROV001 is only for first user registration');
        return false;
      }
    }
    
    try {
      // Check sponsor in all date folders
      const usersRef = ref(database, 'users');
      const usersSnapshot = await get(usersRef);
      
      let sponsorFound = false;
      
      if (usersSnapshot.exists()) {
        const usersByDate = usersSnapshot.val();
        
        // Iterate through all date folders
        for (const date in usersByDate) {
          const users = usersByDate[date];
          
          for (const [, userData] of Object.entries(users)) {
            if (userData.sponsorId && userData.sponsorId.toLowerCase() === sponsorId.toLowerCase()) {
              setSponsorName(userData.name);
              sponsorFound = true;
              setSponsorIdError('');
              break;
            }
          }
          
          if (sponsorFound) break;
        }
        
        if (!sponsorFound) {
          setSponsorName('');
          setSponsorIdError('Sponsor ID not found');
        }
      } else {
        setSponsorName('');
        setSponsorIdError('Sponsor ID not found');
      }
      
      return sponsorFound;
    } catch (error) {
      console.error('Error checking sponsor:', error);
      setSponsorName('');
      setSponsorIdError('Error checking sponsor');
      return false;
    }
  };

  // Check if first user in database
  const checkIfFirstUser = async () => {
    try {
      const usersRef = ref(database, 'users');
      const snapshot = await get(usersRef);
      return !snapshot.exists() || Object.keys(snapshot.val() || {}).length === 0;
    } catch (error) {
      console.error('Error checking first user:', error);
      return false;
    }
  };

  // Validate password strength
  const validateString = (str) => {
    const hasUpper = /[A-Z]/.test(str);
    const hasLower = /[a-z]/.test(str);
    const hasNumber = /[0-9]/.test(str);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(str);
    const hasMinLength = str.length >= 8;
    
    return { hasUpper, hasLower, hasNumber, hasSpecial, hasMinLength };
  };

  // Handle email change
  const handleEmailChange = async (email) => {
    setForm({ ...form, email });
    setEmailError('');
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email && !emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
  };

  // Handle mobile change
  const handleMobileChange = (mobile) => {
    const maxLength = getCountryMaxLength(form.country);
    const digitsOnly = mobile.replace(/\D/g, '');
    const limitedDigits = digitsOnly.slice(0, maxLength);
    
    setForm({ ...form, mobile: limitedDigits });
    if (limitedDigits.length > 0 && limitedDigits.length !== maxLength) {
      setMobileError(`Enter ${maxLength} digits for ${form.country}`);
      return;
    }
    setMobileError('');
  };

  // Handle password change
  const handlePasswordChange = (password) => {
    setForm({ ...form, password });
    setPasswordStrength(validateString(password));
  };

  // Handle name change
  const handleNameChange = (name) => {
    setForm({ ...form, name });
  };

  // Handle sponsor ID change (case insensitive check)
  const handleSponsorIdChange = async (sponsorId) => {
    const upperCaseSponsorId = sponsorId.toUpperCase();
    setForm({ ...form, sponsorId: upperCaseSponsorId });
    
    if (upperCaseSponsorId.trim().length >= 3) {
      await checkSponsorExists(upperCaseSponsorId);
    } else {
      setSponsorName('');
      setSponsorIdError('');
    }
  };

  // Handle country change
  const handleCountryChange = (country) => {
    setForm({ ...form, country, mobile: '' });
    setMobileError('');
  };

  // Validation functions
  const isPasswordValid = () => {
    return Object.values(passwordStrength).every(rule => rule === true);
  };

  const isEmailValid = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(form.email) && !emailError;
  };

  const isMobileValid = () => {
    const maxLength = getCountryMaxLength(form.country);
    return form.mobile.length === maxLength && !mobileError;
  };

  // Save referral to history under sponsor ID with sequential numbers
  // referral history helper (currently unused) - keep for future use
  // eslint-disable-next-line no-unused-vars
  const saveReferralHistory = async (uplineSponsorId, referralData) => {
    try {
      // Get current referrals count for this sponsor
      const sponsorHistoryRef = ref(database, `referralHistory/${uplineSponsorId}`);
      const snapshot = await get(sponsorHistoryRef);
      
      let referralCount = 0;
      if (snapshot.exists()) {
        const referrals = snapshot.val();
        referralCount = Object.keys(referrals).length;
      }
      
      // Save with sequential number as key
      const historyKey = referralCount.toString();
      
      await set(ref(database, `referralHistory/${uplineSponsorId}/${historyKey}`), {
        ...referralData,
        referralNumber: historyKey,
        timestamp: new Date().toISOString(),
        status: 'active'
      });
      
      console.log(`✅ Referral ${historyKey} saved to ${uplineSponsorId}'s history`);
    } catch (error) {
      console.error('Error saving referral history:', error);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!agreeTerms) {
      showDialog('Terms Required', 'Please agree to the terms and conditions to continue.', 'error');
      return;
    }

    if (!agreeRefund) {
      showDialog('Refund Policy Required', 'Please agree to the refund policy to continue.', 'error');
      return;
    }

    // Email validation
    if (!isEmailValid()) {
      showDialog('Invalid Email', 'Please enter a valid email address.', 'error');
      return;
    }

    // Mobile validation
    if (!isMobileValid()) {
      showDialog('Invalid Mobile Number', `Please enter a valid ${form.country} mobile number (${getCountryMaxLength(form.country)} digits).`, 'error');
      return;
    }

    // Sponsor ID validation
    if (!form.sponsorId.trim()) {
      showDialog('Sponsor ID Required', 'Sponsor ID is required for registration.', 'error');
      return;
    }

    // Get current location data
    let currentLocation = registrationLocation;
    if (!currentLocation) {
      try {
        currentLocation = await getCurrentLocation();
        setRegistrationLocation(currentLocation);
      } catch (error) {
        console.error('Failed to get location:', error);
        showDialog('Location Access Required', 'Cannot get your location. Please allow location access to proceed.', 'error');
        return;
      }
    }

    // Check if this is first user
    const isFirstUser = await checkIfFirstUser();
    let finalSponsorId = form.sponsorId.toUpperCase();
    
    if (isFirstUser) {
      // First user gets AGSTRA0001 as upline sponsor
      finalSponsorId = 'AGSTRA0001';
      setForm(prev => ({ ...prev, sponsorId: finalSponsorId }));
      setSponsorName('System Default (First User)');
    } else {
      // Check if sponsor exists - DOWNLINE ACTIVATION CHECK REMOVED
      const sponsorExists = await checkSponsorExists(form.sponsorId);
      
      if (!sponsorExists) {
        showDialog('Sponsor Not Found', 'Sponsor ID not found. Please enter a valid sponsor ID.', 'error');
        return;
      }
    }

    // Password confirmation
    if (form.password !== form.confirmPassword) {
      showDialog('Password Mismatch', 'Passwords do not match! Please enter matching passwords.', 'error');
      return;
    }

    // Password validation
    if (!isPasswordValid()) {
      showDialog('Weak Password', 'Password must contain at least 8 characters including:\n• Uppercase letter (A-Z)\n• Lowercase letter (a-z)\n• Number (0-9)\n• Special character (!@#$%^&*)', 'error');
      return;
    }

    if (!form.name.trim()) {
      showDialog('Name Required', 'Please enter your full name.', 'error');
      return;
    }

    setIsLoading(true);

    try {
      // ensure auth account exists and grab uid
      let firebaseUid = '';
      try {
        const cred = await createUserWithEmailAndPassword(auth, form.email, form.password);
        firebaseUid = cred.user?.uid || '';
        console.log('Firebase auth user created', firebaseUid);
      } catch (err) {
        console.warn('firebase create error', err);
        // if user already exists, try signing in to obtain the uid
        if (err.code === 'auth/email-already-in-use') {
          try {
            const signIn = await signInWithEmailAndPassword(auth, form.email, form.password);
            firebaseUid = signIn.user?.uid || '';
            console.log('Firebase auth existing user signed in', firebaseUid);
          } catch (signErr) {
            console.warn('failed to sign-in existing auth user', signErr);
          }
        }
      }
      // if we still don't have a UID (should be rare), attempt a final sign-in or creation
      if (!firebaseUid && form.email && form.password) {
        try {
          const signIn = await signInWithEmailAndPassword(auth, form.email, form.password);
          firebaseUid = signIn.user?.uid || '';
        } catch (siErr) {
          try {
            const cred2 = await createUserWithEmailAndPassword(auth, form.email, form.password);
            firebaseUid = cred2.user?.uid || '';
          } catch (crtErr) {
            console.warn('final attempt to create/sign-in firebase user failed', crtErr);
          }
        }
        if (firebaseUid) console.log('Firebase uid recovered on fallback', firebaseUid);
      }

      // Generate unique Sponsor ID for new user
      const userSponsorId = await generateUniqueSponsorId();

      
      // Generate unique database key with suffix for same names
      const uniqueDbKey = await generateUniqueDbKey(form.name);
      setGeneratedDbKey(uniqueDbKey);

      // Clean location data to avoid undefined values
      const cleanLocation = {
        ipAddress: currentLocation.ipAddress || '',
        country: currentLocation.country || form.country,
        countryCode: currentLocation.countryCode || '',
        region: currentLocation.region || '',
        city: currentLocation.city || '',
        latitude: currentLocation.latitude || 0,
        longitude: currentLocation.longitude || 0,
        postalCode: currentLocation.postalCode || '',
        timezone: currentLocation.timezone || '',
        isp: currentLocation.isp || '',
        locationSource: currentLocation.locationSource || 'Unknown',
        timestamp: currentLocation.timestamp || new Date().toISOString(),
        gpsLatitude: currentLocation.gpsLatitude || 0,
        gpsLongitude: currentLocation.gpsLongitude || 0,
        gpsAccuracy: currentLocation.gpsAccuracy || 0,
        isGPSAvailable: currentLocation.isGPSAvailable || false
      };

      // User data with real-time registration location - DOWNLINE ACTIVATION STATUS REMOVED
      const userData = {
        sponsorId: userSponsorId,
        uplineSponsorId: finalSponsorId,
        name: form.name,
        email: form.email,
        mobile: form.mobile,
        country: form.country,
        password: form.password,
        registrationDate: new Date().toISOString(),
        role: 'user',
        status: 'inactive',
        firebaseUid: firebaseUid, // store auth UID alongside other details
        investments: [],
        referrals: [],
        lastLogin: null,
        referralLink: `${window.location.origin}/register?sponsor=${userSponsorId}`,
        dbKey: uniqueDbKey,
        // Add real-time registration location data (cleaned)
        registrationLocation: cleanLocation,
        // Initialize login history
        loginHistory: [],
        // Initialize fund with $5 welcome bonus
        fund: {
          balance: 5,
          total: 5,
          count: 1,
          lastAmount: 5,
          lastDate: new Date().toISOString(),
          lastTransactionId: `BONUS_${userSponsorId}`,
          lastTransactionType: 'Welcome Bonus',
          lastTransactionWallet: 'Fund Wallet',
          lastTransactionSource: 'Aerovolt',
          lastPaymentMethod: 'bonus',
          lastPaymentDisplayMethod: 'Registration Welcome Bonus',
          welcomeBonus: true,
          welcomeBonusAmount: 5,
          welcomeBonusDate: new Date().toISOString()
        }
      };

      console.log('📍 Registration location saved:', cleanLocation);
      console.log('💾 Saving user to database with $5 welcome bonus:', userData);

      // Save to database with date-wise structure: users/date/uniqueDbKey
      const date = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
      await set(ref(database, `users/${date}/${uniqueDbKey}`), userData);
      console.log('✅ User saved to Firebase with date-wise structure:', date, uniqueDbKey);

      // Create fund transaction record for $5 welcome bonus with date-wise structure
      await createFundTransactionRecord(userData, 5, 'bonus');
      setBonusStatus('✅ $5 Welcome Bonus added to your fund!');

      // Save to referral history under sponsor's ID (COMMENTED OUT - Hidden)
      // await saveReferralHistory(finalSponsorId, {
      //   uplineSponsorId: finalSponsorId,
      //   uplineSponsorName: sponsorName,
      //   referredSponsorId: userSponsorId,
      //   referredName: form.name,
      //   referredEmail: form.email,
      //   referredMobile: form.mobile,
      //   registrationDate: new Date().toISOString(),
      //   registrationLocation: cleanLocation,
      //   dbKey: uniqueDbKey,
      //   welcomeBonus: 3
      // });

      // Add to sponsor's referrals (only if sponsor exists and not default AGSTRA0001)
      if (finalSponsorId !== 'AGSTRA0001') {
        try {
          const usersRef = ref(database, 'users');
          const usersSnapshot = await get(usersRef);
          
          if (usersSnapshot.exists()) {
            const usersByDate = usersSnapshot.val();
            let sponsorFound = false;
            
            // Search for sponsor in all date folders
            for (const searchDate in usersByDate) {
              const users = usersByDate[searchDate];
              
              for (const [sponsorDbKey, sponsorData] of Object.entries(users)) {
                if (sponsorData.sponsorId && sponsorData.sponsorId.toLowerCase() === finalSponsorId.toLowerCase()) {
                  const referrals = sponsorData.referrals || [];
                  
                  const referralIndex = referrals.length;
                  referrals.push({
                    sponsorId: userSponsorId,
                    name: form.name,
                    mobile: form.mobile,
                    email: form.email,
                    registrationDate: new Date().toISOString(),
                    registrationLocation: cleanLocation,
                    status: 'active',
                    dbKey: uniqueDbKey,
                    index: referralIndex,
                    welcomeBonus: 5
                  });
                  
                  await update(ref(database, `users/${searchDate}/${sponsorDbKey}`), {
                    referrals: referrals,
                    referralCount: referrals.length
                  });
                  console.log('✅ Added to sponsor referrals, index:', referralIndex);
                  sponsorFound = true;
                  break;
                }
              }
              
              if (sponsorFound) break;
            }
          }
        } catch (sponsorError) {
          console.log('Note: Error adding to sponsor referrals', sponsorError);
        }
      }

     // Send welcome email
console.log('📨 Sending welcome email...');
await sendWelcomeEmail(userData, finalSponsorId);
setEmailSent(true);
      
      // Show success dialog with $5 bonus info
      setTimeout(() => {
        showDialog(
          '🎉 Registration Successful!',
          `Your Sponsor ID: ${userSponsorId}\n\nWelcome Bonus: $5 added to your Fund Balance\n\nLogin to access your account and start investing!`
        );
        
        // Redirect to login page after dialog is closed
        setTimeout(() => {
          if (dialog.isOpen) {
            const closeHandler = () => {
              navigate(`/login?sponsor=${userSponsorId}`, { state: { password: form.password } });
              closeDialog();
            };
            // Override close function to redirect
            setDialog(prev => ({
              ...prev,
              onClose: closeHandler
            }));
          } else {
            navigate(`/login?sponsor=${userSponsorId}`, { state: { password: form.password } });
          }
        }, 3000);
      }, 1500);
      
    } catch (error) {
      console.error('Registration error:', error);
      showDialog('Registration Failed', 'Registration failed. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Check sponsor from URL
  useEffect(() => {
    const checkSponsorFromURL = async () => {
      if (form.sponsorId) {
        await checkSponsorExists(form.sponsorId);
      }
    };
    
    checkSponsorFromURL();
  }, [form.sponsorId, checkSponsorExists]);

  // Get mobile format hint
  const getMobileFormatHint = () => {
    const maxLength = getCountryMaxLength(form.country);
    return `${maxLength} digits`;
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center pt-20 md:pt-32 px-4 py-8 overflow-hidden">
      {/* <WaveBackground /> */}
      
      {/* Dialog Component */}
      <Dialog
        isOpen={dialog.isOpen}
        onClose={closeDialog}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative z-10 w-full max-w-lg"
      >
        <div className="glass-card p-6 md:p-8 backdrop-blur-lg card-glow">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-foreground font-heading">Create Account</h1>
            <p className="text-muted-foreground mt-2">Start your investment journey with $5 welcome bonus</p>
          </div>

          {locationLoading && (
            <div className="mb-4 p-2 bg-blue-50 dark:bg-blue-900/10 rounded border border-blue-200 dark:border-blue-800 card-glow">
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500"></div>
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  🔍 Detecting your location...
                </p>
              </div>
            </div>
          )}

          {locationError && (
            <div className="mb-4 p-2 bg-yellow-50 dark:bg-yellow-900/10 rounded border border-yellow-200 dark:border-yellow-800 card-glow">
              <div className="flex items-start gap-2">
                <div className="text-yellow-500 mt-0.5 text-sm">⚠️</div>
                <div>
                  <p className="text-xs font-medium text-yellow-800 dark:text-yellow-300">Location Notice</p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-0.5">{locationError}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 mt-4 md:mt-0">
            {/* Sponsor ID and Full Name Side by Side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Sponsor ID Field */}
              <div>
                <label className="block text-xs md:text-sm font-medium mb-1.5 font-heading text-foreground">
                  Upline Sponsor ID *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Sponsor ID"
                    value={form.sponsorId}
                    onChange={(e) => handleSponsorIdChange(e.target.value)}
                    required
                    readOnly={!!queryParams.get('sponsor')}
                    className={`w-full px-3 md:px-4 py-2.5 rounded-lg bg-secondary/80 border ${
                      sponsorIdError ? 'border-red-500' : 
                      queryParams.get('sponsor') ? 'border-green-500 bg-green-50/20' : 'border-border'
                    } focus:border-primary focus:outline-none ${queryParams.get('sponsor') ? 'cursor-not-allowed' : ''} pr-10 uppercase font-body text-foreground text-sm`}
                  />
                  <Users className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                </div>
                {sponsorName && (
                  <p className="text-xs text-green-500 mt-1 flex items-center gap-1">
                    <Check size={12} /> Sponsor: {sponsorName}
                  </p>
                )}
                {sponsorIdError && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <X size={12} /> {sponsorIdError}
                  </p>
                )}
              </div>

              {/* Name Field */}
              <div>
                <label className="block text-xs md:text-sm font-medium mb-1.5 font-heading text-foreground">Full Name *</label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={form.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    required
                    className="w-full px-3 md:px-4 py-2.5 rounded-lg bg-secondary/80 border border-border focus:border-primary focus:outline-none pr-10 font-body text-foreground text-sm"
                  />
                  <User className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                </div>
              </div>
            </div>

            {/* Email and Country Side by Side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Email Field */}
              <div>
                <label className="block text-xs md:text-sm font-medium mb-1.5 font-heading text-foreground">
                  Email Address *
                </label>
                <div className="relative">
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={form.email}
                    onChange={(e) => handleEmailChange(e.target.value)}
                    required
                    className={`w-full px-3 md:px-4 py-2.5 rounded-lg bg-secondary/80 border ${
                      emailError ? 'border-red-500' : 'border-border'
                    } focus:border-primary focus:outline-none pr-10 font-body text-foreground text-sm`}
                  />
                  <Mail className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                </div>
              </div>

              {/* Country Field */}
              <div>
                <label className="block text-xs md:text-sm font-medium mb-1.5 font-heading text-foreground">Country *</label>
                <div className="relative">
                  <select
                    value={form.country}
                    onChange={(e) => handleCountryChange(e.target.value)}
                    required
                    className="w-full px-3 md:px-4 py-2.5 rounded-lg bg-secondary/80 border border-border focus:border-primary focus:outline-none pr-10 appearance-none font-body text-foreground text-sm"
                  >
                    {countries.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                  <Globe className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" size={16} />
                </div>
              </div>
            </div>

            {/* Mobile Field - Full Width */}
            <div>
              <label className="block text-xs md:text-sm font-medium mb-1.5 font-heading text-foreground">
                Mobile No * <span className="text-xs text-muted-foreground">({getMobileFormatHint()})</span>
              </label>
              <div className="relative">
                <input
                  ref={mobileInputRef}
                  type="tel"
                  placeholder={`Enter ${getCountryMaxLength(form.country)} digit mobile number`}
                  value={form.mobile}
                  onChange={(e) => handleMobileChange(e.target.value)}
                  maxLength={getCountryMaxLength(form.country)}
                  required
                  className={`w-full px-3 md:px-4 py-2.5 rounded-lg bg-secondary/80 border ${
                    mobileError ? 'border-red-500' : 'border-border'
                  } focus:border-primary focus:outline-none font-body text-foreground text-sm`}
                />
                <Phone className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-xs text-muted-foreground">
                  {form.mobile.length}/{getCountryMaxLength(form.country)} digits
                </span>
              </div>
            </div>

            {/* Password and Confirm Password Side by Side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Password Field */}
              <div>
                <label className="block text-xs md:text-sm font-medium mb-1.5 font-heading text-foreground">
                  Password *
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    value={form.password}
                    onChange={(e) => handlePasswordChange(e.target.value)}
                    required
                    className="w-full px-3 md:px-4 py-2.5 rounded-lg bg-secondary/80 border border-border focus:border-primary focus:outline-none pr-10 font-body text-foreground text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                
                {/* Password Strength Meter */}
                {form.password && (
                  <div className="mt-2 space-y-1.5 p-2 bg-secondary/50 rounded card-glow">
                    <div className="flex items-center space-x-1.5">
                      {passwordStrength.hasMinLength ? 
                        <Check size={14} className="text-green-500" /> : 
                        <X size={14} className="text-red-500" />
                      }
                      <span className={`text-xs ${passwordStrength.hasMinLength ? 'text-green-500' : 'text-red-500'} font-body`}>
                        8+ characters
                      </span>
                    </div>
                    <div className="flex items-center space-x-1.5">
                      {passwordStrength.hasUpper ? 
                        <Check size={14} className="text-green-500" /> : 
                        <X size={14} className="text-red-500" />
                      }
                      <span className={`text-xs ${passwordStrength.hasUpper ? 'text-green-500' : 'text-red-500'} font-body`}>
                        Uppercase (A-Z)
                      </span>
                    </div>
                    <div className="flex items-center space-x-1.5">
                      {passwordStrength.hasLower ? 
                        <Check size={14} className="text-green-500" /> : 
                        <X size={14} className="text-red-500" />
                      }
                      <span className={`text-xs ${passwordStrength.hasLower ? 'text-green-500' : 'text-red-500'} font-body`}>
                        Lowercase (a-z)
                      </span>
                    </div>
                    <div className="flex items-center space-x-1.5">
                      {passwordStrength.hasNumber ? 
                        <Check size={14} className="text-green-500" /> : 
                        <X size={14} className="text-red-500" />
                      }
                      <span className={`text-xs ${passwordStrength.hasNumber ? 'text-green-500' : 'text-red-500'} font-body`}>
                        Number (0-9)
                      </span>
                    </div>
                    <div className="flex items-center space-x-1.5">
                      {passwordStrength.hasSpecial ? 
                        <Check size={14} className="text-green-500" /> : 
                        <X size={14} className="text-red-500" />
                      }
                      <span className={`text-xs ${passwordStrength.hasSpecial ? 'text-green-500' : 'text-red-500'} font-body`}>
                        Special (!@#$%^&*)
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Confirm Password Field */}
              <div>
                <label className="block text-xs md:text-sm font-medium mb-1.5 font-heading text-foreground">Confirm Password *</label>
                <div className="relative">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    placeholder="Confirm Password"
                    value={form.confirmPassword}
                    onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                    required
                    className="w-full px-3 md:px-4 py-2.5 rounded-lg bg-secondary/80 border border-border focus:border-primary focus:outline-none pr-10 font-body text-foreground text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {form.confirmPassword && form.password !== form.confirmPassword && (
                  <p className="text-red-500 text-xs mt-1 font-body">Passwords do not match</p>
                )}
                {form.confirmPassword && form.password === form.confirmPassword && (
                  <p className="text-green-500 text-xs mt-1 font-body">✓ Passwords match</p>
                )}
              </div>
            </div>

            {/* Terms & Conditions and Refund Policy in one row */}
            <div className="space-y-2 pt-2">
              <div className="flex items-start space-x-2">
                <input
                  type="checkbox"
                  id="terms"
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                  className="w-4 h-4 rounded border-border mt-0.5"
                  required
                />
                <label htmlFor="terms" className="text-xs text-muted-foreground font-body flex-1">
                  I agree to the{' '}
                  <Link to="/terms" target="_blank" className="text-primary hover:underline font-medium">
                    Terms & Conditions
                  </Link>
                  {' '}and{' '}
                  <Link to="/privacy" target="_blank" className="text-primary hover:underline font-medium">
                    Privacy Policy
                  </Link>
                </label>
              </div>

              <div className="flex items-start space-x-2">
                <input
                  type="checkbox"
                  id="refund"
                  checked={agreeRefund}
                  onChange={(e) => setAgreeRefund(e.target.checked)}
                  className="w-4 h-4 rounded border-border mt-0.5"
                  required
                />
                <label htmlFor="refund" className="text-xs text-muted-foreground font-body flex-1">
                  I have read and agree to the{' '}
                  <Link to="/refund-policy" target="_blank" className="text-primary hover:underline font-medium">
                    Refund Policy
                  </Link>
                </label>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || emailLoading || !isPasswordValid() || !isEmailValid() || !isMobileValid() || !agreeTerms || !agreeRefund}
              className={`w-full py-3 text-sm md:text-base font-medium rounded-lg flex items-center justify-center gap-2 ${
                isLoading || emailLoading || !isPasswordValid() || !isEmailValid() || !isMobileValid() || !agreeTerms || !agreeRefund
                  ? 'opacity-70 cursor-not-allowed bg-gray-600 text-gray-400' 
                  : 'btn-electric hover:scale-[1.02] transition-transform'
              }`}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Creating Account...
                </>
              ) : (
                <>
                  <Gift size={18} />
                  Create Account & Get $5 Bonus
                </>
              )}
            </button>
          </form>

          {/* Already Registered Link */}
          <p className="text-center mt-6 text-xs md:text-sm text-muted-foreground font-body">
            Already Registered?{' '}
            <Link to="/login" className="text-primary font-medium hover:underline">
              Login Here
            </Link>
          </p>

          {/* Terms & Policies Links */}
          <div className="text-center mt-4 pt-4 border-t border-border">
            <div className="flex flex-wrap justify-center gap-3 md:gap-4 text-xs text-muted-foreground font-body">
              <Link to="/terms" target="_blank" className="hover:text-primary hover:underline flex items-center gap-1">
                <FileText size={12} />
                Terms
              </Link>
              <Link to="/privacy" target="_blank" className="hover:text-primary hover:underline flex items-center gap-1">
                <Shield size={12} />
                Privacy
              </Link>
              <Link to="/refund-policy" target="_blank" className="hover:text-primary hover:underline flex items-center gap-1">
                <FileText size={12} />
                Refund
              </Link>
            </div>
          </div>

          {/* Footer Links */}
          <div className="text-center mt-6 text-xs text-muted-foreground font-body">
            <Link to="/" className="hover:text-primary underline">
              Back To Home
            </Link>
            <p className="mt-2 text-foreground">Aerovolt Exchange © 2026 . All Rights Reserved.</p>
          </div>
          
        </div>
      </motion.div>
    </div>
  );
};

export default Register;

